import type { IconName } from "lucide-react/dynamic";

import type { Icon } from "@/lib/api/types";

/**
 * Navigation element `kind` values. The spec dropped `kind` (2026-10); the backend will
 * bring it back (values may change). Until then only the frontend built-in warning pages
 * carry one, so always read it through `navigationElementKind`.
 */
export const NAVIGATION_ELEMENT_KIND = {
  folder: "folder",
  page: "page",
  /** Risk-picture page (`/land-warnings` content). */
  publicWarningsPage: "public_warnings_page",
  /** Maritime warnings page (coastal / North Sea map). */
  maritimeWarningsPage: "maritime_warnings_page",
} as const satisfies Record<string, string>;

/** An API navigation element plus the `kind` the spec no longer declares. */
export type KindedNavigationElement<T> = T & { kind?: string };

/** `kind` when the element carries one (built-ins, or a backend that sends it again). */
export function navigationElementKind(element: object | undefined): string | undefined {
  if (!element || !("kind" in element)) return undefined;
  return typeof element.kind === "string" ? element.kind : undefined;
}

const BUILT_IN_PAGE_KINDS: readonly string[] = [
  NAVIGATION_ELEMENT_KIND.publicWarningsPage,
  NAVIGATION_ELEMENT_KIND.maritimeWarningsPage,
];

/** Built-in pages have no CMS `page_id`, cannot be nested, and only edit their title. */
export function isBuiltInPageKind(kind: string | undefined): boolean {
  return kind !== undefined && BUILT_IN_PAGE_KINDS.includes(kind);
}

/**
 * Folders have no CMS `page_id`; built-in pages neither, so they are excluded. Does not
 * rely on `kind`, which the backend does not send for now.
 */
export function isFolderNavigationElement(
  element: KindedNavigationElement<{ page_id?: string }>,
): boolean {
  return !element.page_id && !isBuiltInPageKind(navigationElementKind(element));
}

/** Top-level only: built-in pages cannot be nested in a folder. */
export function findNavigationElementByKind<T extends object>(
  elements: T[] | undefined,
  kind: string,
): T | undefined {
  return elements?.find((element) => navigationElementKind(element) === kind);
}

/**
 * Interim, until the backend creates warning page navigation elements: appends each
 * `builtIn` whose `kind` the backend did not send. A backend element always wins.
 */
export function withBuiltInWarningPages<T extends object>(
  elements: T[],
  builtIns: T[],
): T[] {
  const missing = builtIns.filter(
    (builtIn) =>
      !findNavigationElementByKind(elements, navigationElementKind(builtIn) ?? ""),
  );
  return missing.length > 0 ? [...elements, ...missing] : elements;
}

/** Default icon of the built-in warning pages (lucide "triangle-alert"). */
export const WARNING_PAGE_ICON = "triangle-alert" satisfies IconName;

/**
 * Icon to show for a navigation element: its own `icon`, else the warning triangle for
 * built-in warning pages. Undefined lets callers keep their own fallback (file/folder).
 */
export function navigationElementIcon(
  element: KindedNavigationElement<{ icon?: Icon }>,
): Icon | IconName | undefined {
  if (element.icon) return element.icon;
  return isBuiltInPageKind(navigationElementKind(element)) ? WARNING_PAGE_ICON : undefined;
}

/** "Show page" switch per built-in warning page; `undefined` while settings are loading. */
export interface WarningPageVisibility {
  publicWarningsPage: boolean | undefined;
  maritimeWarningsPage: boolean | undefined;
}

/**
 * Drops built-in warning pages switched off in their settings. Unknown visibility keeps
 * the page, so a failed settings request never hides it. Other kinds always stay.
 */
export function withoutHiddenWarningPages<T extends object>(
  elements: T[],
  visibility: WarningPageVisibility,
): T[] {
  return elements.filter((element) => {
    const kind = navigationElementKind(element);
    if (kind === NAVIGATION_ELEMENT_KIND.publicWarningsPage) {
      return visibility.publicWarningsPage !== false;
    }
    if (kind === NAVIGATION_ELEMENT_KIND.maritimeWarningsPage) {
      return visibility.maritimeWarningsPage !== false;
    }
    return true;
  });
}
